import { useEffect, useState } from "react";
import { Sparkles, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import ProductCard from "@/components/store/ProductCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const RECIPIENTS = ["Myself", "Parents", "Spouse / Partner", "Children", "Business / Colleagues", "Friends"];
const BUDGETS = [
  { key: "under-5k", label: "Under ₹5,000", max: 5000 },
  { key: "5k-15k", label: "₹5,000 – ₹15,000", min: 5000, max: 15000 },
  { key: "15k-50k", label: "₹15,000 – ₹50,000", min: 15000, max: 50000 },
  { key: "above-50k", label: "Above ₹50,000", min: 50000 },
];

export default function CoinSelector() {
  const [occasions, setOccasions] = useState([]);
  const [occasion, setOccasion] = useState(null);
  const [recipient, setRecipient] = useState("");
  const [budget, setBudget] = useState(null);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [consent, setConsent] = useState(false);
  const [leadBusy, setLeadBusy] = useState(false);
  const [leadDone, setLeadDone] = useState(false);

  useEffect(() => {
    document.title = "Which coin for which occasion? — Guided Selector | Siyaara";
    api.get("/coin-merch/occasion").then((r) => setOccasions(r.data?.cards || [])).catch(() => {});
  }, []);

  const recommend = async () => {
    if (!occasion) { toast.error("Pick an occasion to begin"); return; }
    setLoading(true); setResults(null);
    try {
      const r = await api.get(`/facets/${occasion.key}`);
      let prods = r.data?.products || [];
      if (budget) {
        prods = prods.filter((p) => {
          const price = p.price_min || 0;
          if (budget.min && price < budget.min) return false;
          if (budget.max && price > budget.max) return false;
          return true;
        });
      }
      setResults(prods);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const submitLead = async (e) => {
    e.preventDefault();
    if (!name.trim() || !phone.trim()) { toast.error("Name and phone are required"); return; }
    if (!consent) { toast.error("Please tick the consent box so we can help you"); return; }
    setLeadBusy(true);
    try {
      await api.post("/coin-guide", {
        occasion: occasion?.label || "", recipient, budget: budget?.label || "",
        name: name.trim(), phone: phone.trim(), consent,
      });
      setLeadDone(true);
      toast.success("Thank you — our team will help you pick the perfect coin.");
    } catch {
      toast.error("Could not submit — please try again");
    } finally {
      setLeadBusy(false);
    }
  };

  return (
    <div className="container-x py-12 max-w-5xl" data-testid="coin-selector-page">
      <p className="eyebrow flex items-center gap-2"><Sparkles size={13} className="text-gold" /> Guided coin selector</p>
      <h1 className="font-heading text-4xl sm:text-5xl font-semibold mt-2">Which coin for which occasion?</h1>
      <p className="text-stone-600 mt-3 max-w-2xl">
        Tell us the moment and who it's for — we'll suggest 999 fine coins that fit, every one assay-certified and QR-verifiable.
      </p>

      <div className="mt-8 border border-stone-200 bg-white p-6 sm:p-8 rounded-xl" data-testid="coin-selector-form">
        <label className="eyebrow">Occasion</label>
        <div className="mt-2 flex flex-wrap gap-2" data-testid="selector-occasions">
          {occasions.length === 0 && <p className="text-sm text-stone-400">Loading occasions…</p>}
          {occasions.map((o) => (
            <button key={o.key} type="button" onClick={() => setOccasion(o)}
              className={`px-3 py-2 text-xs border rounded-full transition-colors ${occasion?.key === o.key ? "border-brand bg-brand text-ivory" : "border-stone-300 hover:border-brand"}`}
              data-testid={`selector-occasion-${o.key}`}>{o.label}</button>
          ))}
        </div>

        <div className="grid sm:grid-cols-2 gap-6 mt-6">
          <div>
            <label className="eyebrow">Who is it for?</label>
            <div className="mt-2 flex flex-wrap gap-2" data-testid="selector-recipients">
              {RECIPIENTS.map((r) => (
                <button key={r} type="button" onClick={() => setRecipient(r)}
                  className={`px-3 py-2 text-xs border rounded-full transition-colors ${recipient === r ? "border-brand bg-brand text-ivory" : "border-stone-300 hover:border-brand"}`}
                  data-testid={`selector-recipient-${r.toLowerCase().replace(/[\s/]+/g, "-")}`}>{r}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="eyebrow">Budget</label>
            <div className="mt-2 flex flex-wrap gap-2" data-testid="selector-budgets">
              {BUDGETS.map((b) => (
                <button key={b.key} type="button" onClick={() => setBudget(b)}
                  className={`px-3 py-2 text-xs border rounded-full transition-colors ${budget?.key === b.key ? "border-brand bg-brand text-ivory" : "border-stone-300 hover:border-brand"}`}
                  data-testid={`selector-budget-${b.key}`}>{b.label}</button>
              ))}
            </div>
          </div>
        </div>

        <Button onClick={recommend} disabled={loading} className="mt-6 bg-brand text-ivory hover:bg-brand-2 rounded-full px-8 btn-press" data-testid="selector-recommend-btn">
          {loading ? "Finding…" : "Show me coins"}
        </Button>
      </div>

      {results !== null && (
        <div className="mt-10" data-testid="selector-results">
          <h2 className="font-heading text-2xl font-semibold">
            {results.length > 0 ? `Recommended for ${occasion?.label}` : "No exact matches"}
          </h2>
          {results.length === 0 ? (
            <p className="text-stone-500 mt-2">Nothing fit those exact filters — try a wider budget, or leave your details below and we'll curate a few by hand.</p>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
              {results.slice(0, 8).map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          )}
        </div>
      )}

      <form onSubmit={submitLead} className="mt-10 border border-stone-200 bg-white p-6 sm:p-8 rounded-xl" data-testid="selector-lead-form">
        <p className="eyebrow mb-1">Prefer a human touch?</p>
        <p className="text-sm text-stone-600 mb-4">Leave your details and our team will hand-pick coins for your occasion — no obligation.</p>
        {leadDone ? (
          <p className="text-sm text-emerald-700" data-testid="selector-lead-done">Done — we'll be in touch shortly.</p>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 gap-3">
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" data-testid="selector-name" />
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone (e.g. +91 98765 43210)" data-testid="selector-phone" />
            </div>
            <label className="mt-3 flex items-start gap-2 text-xs text-stone-600 cursor-pointer">
              <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5" data-testid="selector-consent" />
              <span>I agree to Siyaara contacting me about this enquiry. My details are used only for this conversation.</span>
            </label>
            <Button type="submit" disabled={leadBusy} className="mt-4 bg-stone-900 text-white hover:bg-brand rounded-full px-8 btn-press" data-testid="selector-lead-submit">
              {leadBusy ? "Sending…" : "Help me choose"}
            </Button>
          </>
        )}
        <div className="mt-4 flex items-start gap-2 text-xs text-stone-500">
          <ShieldAlert size={14} className="text-gold shrink-0 mt-0.5" />
          <p>Coins are offered for gifting and the joy of the occasion. We make no claim of luck, wealth or any outcome.</p>
        </div>
      </form>
    </div>
  );
}
